// frontend/src/hooks/bookingManagement/useScheduleAvailability.js

import { useCallback, useEffect, useRef, useState } from 'react';
import axios from '../../axiosConfig';
import { toast } from 'react-toastify';
import {
  generateTimeSlotsFromSchedule,
  getScheduleAvailableDates,
  isDateExcluded,
  isDateInRange,
  isDayIncluded,
  normalizeBookingOccurrenceDate,
} from '../../utils/bookingScheduleUtils';
import { formatDateISO } from '../../utils/dateTimeFormatter';

const noop = () => {};

const toMinutes = (timeValue) => {
  if (!timeValue || typeof timeValue !== 'string') {
    return null;
  }
  const [hours, minutes] = timeValue.split(':').map((value) => parseInt(value, 10));
  if ([hours, minutes].some(Number.isNaN)) {
    return null;
  }
  return hours * 60 + minutes;
};

const toTimeString = (totalMinutes) => {
  if (!Number.isInteger(totalMinutes) || totalMinutes < 0) {
    return null;
  }
  const hours = Math.floor(totalMinutes / 60) % 24;
  const minutes = totalMinutes % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

export const buildCandidateTimes = (slots = []) => {
  const values = new Set();
  slots.forEach((slot) => {
    if (slot?.type === 'fixed' && slot.time) {
      values.add(slot.time);
      return;
    }
    if (slot?.type === 'on_call' && slot.startTime && slot.endTime) {
      // The compatibility capacity endpoint still needs representative
      // instants to verify accessible service when no trip exists yet.
      values.add(slot.startTime);
      values.add(slot.endTime);
      const startMinutes = toMinutes(slot.startTime);
      const endMinutes = toMinutes(slot.endTime);
      if (startMinutes != null && endMinutes != null && endMinutes > startMinutes) {
        values.add(toTimeString(startMinutes + Math.floor((endMinutes - startMinutes) / 2)));
      }
    }
  });
  return Array.from(values).filter(Boolean).sort();
};

// Riders and luggage are part of the availability authority query, so an
// out-of-range count makes the query itself invalid. A blank field is still
// mid-edit and falls back to the form default; anything else is a form error the
// caller must name, because a rejected authority read only reports a generic
// transport failure.
export const resolveAvailabilityQuantities = ({ riders, luggage } = {}) => {
  const ridersInput = String(riders ?? '').trim();
  const luggageInput = String(luggage ?? '').trim();
  const parsedRiders = ridersInput === '' ? 1 : parseInt(ridersInput, 10);
  const parsedLuggages = luggageInput === '' ? 0 : parseInt(luggageInput, 10);
  if (!Number.isInteger(parsedRiders) || parsedRiders < 1) {
    return { errorKey: 'Please enter a valid number of riders' };
  }
  if (!Number.isInteger(parsedLuggages) || parsedLuggages < 0) {
    return { errorKey: 'luggageInvalidNumber' };
  }
  return { riders: parsedRiders, luggages: parsedLuggages };
};

const operationalSlotDisplayKey = (slot) => (
  slot?.type === 'on_call'
    ? `on_call:${slot.startTime}:${slot.endTime}`
    : `fixed:${slot?.time}`
);

const operationalSlotIdentityKey = (slot) => {
  const scheduleId = slot?.scheduleId ?? slot?.occurrenceScheduleId ?? null;
  return `${operationalSlotDisplayKey(slot)}:schedule:${scheduleId ?? 'unresolved'}`;
};

const operationalTimeToMinutes = (value, { allowExclusiveEnd = false } = {}) => {
  const match = String(value || '').match(/^(\d{2}):(\d{2})$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (minutes > 59) return null;
  if (allowExclusiveEnd && hours === 24 && minutes === 0) return 24 * 60;
  if (hours > 23) return null;
  return hours * 60 + minutes;
};

// Unlike the candidate-time helper above, interval boundaries must preserve
// 1440 as the exclusive "24:00" sentinel instead of wrapping to "00:00".
const operationalMinutesToTime = (minutes) => (
  `${String(Math.floor(minutes / 60)).padStart(2, '0')}:`
  + `${String(minutes % 60).padStart(2, '0')}`
);

const mergeMinuteIntervals = (intervals) => {
  const merged = [];
  [...intervals]
    .sort((left, right) => left.start - right.start || left.end - right.end)
    .forEach((interval) => {
      const previous = merged.at(-1);
      if (previous && interval.start <= previous.end) {
        previous.end = Math.max(previous.end, interval.end);
      } else {
        merged.push({ ...interval });
      }
    });
  return merged;
};

const intersectMinuteIntervals = (left, right) => {
  const intersections = [];
  let leftIndex = 0;
  let rightIndex = 0;
  while (leftIndex < left.length && rightIndex < right.length) {
    const start = Math.max(left[leftIndex].start, right[rightIndex].start);
    const end = Math.min(left[leftIndex].end, right[rightIndex].end);
    if (end > start) intersections.push({ start, end });
    if (left[leftIndex].end < right[rightIndex].end) {
      leftIndex += 1;
    } else {
      rightIndex += 1;
    }
  }
  return mergeMinuteIntervals(intersections);
};

const normalizeOperationalIntervals = (state, domain) => {
  const fullDomain = [{ ...domain }];
  if (!state || state.operationallyAvailable !== true) {
    return fullDomain;
  }
  if (!Array.isArray(state.operationalBlockedIntervals)) {
    return fullDomain;
  }

  const normalized = [];
  for (const interval of state.operationalBlockedIntervals) {
    const start = operationalTimeToMinutes(interval?.startTime);
    const end = operationalTimeToMinutes(interval?.endTime, { allowExclusiveEnd: true });
    if (
      start == null
      || end == null
      || end <= start
      || start < domain.start
      || end > domain.end
    ) {
      // A malformed authority payload must not become "no blocks". The full
      // domain is the safe identity for book-all intersection and forces
      // selected-days union to fail closed.
      return fullDomain;
    }
    normalized.push({ start, end });
  }
  return mergeMinuteIntervals(normalized);
};

const aggregateOnCallIntervals = ({ states, representativeState, bookAllAvailableDates }) => {
  const start = operationalTimeToMinutes(representativeState.startTime);
  const inclusiveEnd = operationalTimeToMinutes(representativeState.endTime);
  if (start == null || inclusiveEnd == null || inclusiveEnd < start) {
    return {
      intervals: [],
      fullyBlocked: true,
    };
  }
  const domain = { start, end: inclusiveEnd + 1 };

  let aggregated;
  if (bookAllAvailableDates) {
    const eligibleStates = states.filter((state) => (
      state?.operationallyAvailable === true
      && state?.assignmentAvailable !== false
    ));
    aggregated = eligibleStates.length > 0
      ? eligibleStates
        .map((state) => normalizeOperationalIntervals(state, domain))
        .reduce((current, intervals) => (
          current == null ? intervals : intersectMinuteIntervals(current, intervals)
        ), null)
      : [];
  } else {
    aggregated = mergeMinuteIntervals(
      states.flatMap((state) => normalizeOperationalIntervals(state, domain)),
    );
  }

  const intervals = aggregated || [];
  const fullyBlocked = (
    intervals.length === 1
    && intervals[0].start <= domain.start
    && intervals[0].end >= domain.end
  );
  return {
    intervals: intervals.map((interval) => ({
      startTime: operationalMinutesToTime(interval.start),
      endTime: operationalMinutesToTime(interval.end),
    })),
    fullyBlocked,
  };
};

export const aggregateOperationalStates = ({
  primaryStates,
  occurrenceDates,
  occurrences,
  bookAllAvailableDates,
}) => {
  const representativeStates = new Map();
  // Preserve the primary date's winning schedule for each visible time/window.
  // Later dates may contribute new display times, but may not replace that
  // occurrence identity merely because they were processed later.
  [...primaryStates, ...occurrenceDates.flatMap((date) => occurrences[date] || [])]
    .forEach((state) => {
      const displayKey = operationalSlotDisplayKey(state);
      if (!representativeStates.has(displayKey)) {
        representativeStates.set(displayKey, state);
      }
    });

  return Array.from(representativeStates.values()).map((representativeState) => {
    const key = operationalSlotIdentityKey(representativeState);
    const states = occurrenceDates.map((date) => (
      (occurrences[date] || []).find(
        (entry) => operationalSlotIdentityKey(entry) === key
      ) || null
    ));
    const everyOrSome = bookAllAvailableDates ? 'some' : 'every';
    const occurrenceAvailable = states[everyOrSome]((state) => (
      state?.operationallyAvailable === true
      && state?.assignmentAvailable !== false
      && state?.bookingStopEligibility?.state === 'available'
    ));
    const operationallyAvailable = states[everyOrSome](
      (state) => state?.operationallyAvailable === true,
    );
    const onCallIntervals = representativeState.type === 'on_call'
      ? aggregateOnCallIntervals({
        states,
        representativeState,
        bookAllAvailableDates,
      })
      : null;
    const aggregateOperationallyAvailable = (
      operationallyAvailable && !onCallIntervals?.fullyBlocked
    );
    const aggregateOccurrenceAvailable = (
      occurrenceAvailable && !onCallIntervals?.fullyBlocked
    );
    const stopEligible = states[everyOrSome](
      (state) => state?.bookingStopEligibility?.state === 'available'
    );
    const unavailableEndpoints = [...new Set(states.flatMap(
      (state) => state?.bookingStopEligibility?.endpoints || []
    ))];
    return {
      ...representativeState,
      operationallyAvailable: aggregateOperationallyAvailable,
      operationalUnavailabilityScope: onCallIntervals?.fullyBlocked
        ? 'route'
        : (
          onCallIntervals && aggregateOperationallyAvailable
            ? null
            : representativeState.operationalUnavailabilityScope
        ),
      ...(onCallIntervals ? {
        operationalBlockedIntervals: onCallIntervals.intervals,
      } : {}),
      assignmentAvailable: aggregateOccurrenceAvailable
        ? true
        : (
          aggregateOperationallyAvailable
            ? false
            : representativeState.assignmentAvailable
        ),
      occurrenceAvailable: aggregateOccurrenceAvailable,
      bookingStopEligibility: stopEligible
        ? { state: 'available', reason: null, endpoints: [] }
        : {
          state: 'unavailable',
          reason: representativeState.bookingStopEligibility?.reason || 'order_invalid',
          endpoints: unavailableEndpoints
        },
    };
  });
};

export const selectOperationalOccurrenceDates = ({
  availableDates,
  selectedDates,
  targetDate,
  bookAllAvailableDates,
  canBookRecurring,
}) => {
  if (bookAllAvailableDates) {
    // Admission independently recomputes this inclusive 14-day horizon under
    // locked schedule definitions and requires exact expected-map equality.
    const horizon = new Date(`${targetDate}T00:00:00Z`);
    horizon.setUTCDate(horizon.getUTCDate() + 14);
    const inclusiveEnd = horizon.toISOString().slice(0, 10);
    return availableDates.filter((date) => date >= targetDate && date <= inclusiveEnd);
  }
  return canBookRecurring && selectedDates.length > 0
    ? selectedDates
    : [targetDate];
};

const useScheduleAvailability = ({
  currentEntity,
  booking,
  setBooking,
  canBookRecurring,
  bookingWindowDays,
  setLoading,
  t,
  setIsWaitlisted,
  addProcessingDetail = noop,
}) => {
  const [appliedSchedules, setAppliedSchedules] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [availableTimeSlots, setAvailableTimeSlots] = useState([]);
  const [selectedDates, setSelectedDates] = useState([]);
  const [bookAllAvailableDates, setBookAllAvailableDates] = useState(false);
  const [slotAvailability, setSlotAvailability] = useState([]);
  const [slotAvailabilityLoading, setSlotAvailabilityLoading] = useState(false);
  const [slotAvailabilityDate, setSlotAvailabilityDate] = useState(null);
  const [occurrencePreview, setOccurrencePreview] = useState({});
  const availabilityRequestGenerationRef = useRef(0);

  useEffect(() => () => {
    availabilityRequestGenerationRef.current += 1;
  }, []);

  const generateAvailableDates = useCallback(
    (schedules) => {
      if (!schedules?.length) {
        addProcessingDetail('No schedules found');
        setAvailableDates([]);
        return;
      }

      const dates = new Set();
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const futureDate = new Date();
      futureDate.setDate(futureDate.getDate() + bookingWindowDays);
      futureDate.setHours(23, 59, 59, 999);

      schedules.forEach((schedule) => {
        addProcessingDetail(`Processing schedule: ${schedule.name} (Priority: ${schedule.priority})`);
        const scheduleDates = getScheduleAvailableDates(schedule, today, futureDate);
        addProcessingDetail(`Found ${scheduleDates.size} dates for ${schedule.name}`);
        scheduleDates.forEach((date) => dates.add(date));
      });

      addProcessingDetail(`Total available dates: ${dates.size}`);
      setAvailableDates(Array.from(dates).sort());
    },
    [addProcessingDetail, bookingWindowDays],
  );

  const resolvePrimaryDate = useCallback(() => {
    if (bookAllAvailableDates && availableDates.length > 0) {
      return availableDates[0];
    }

    if (canBookRecurring && selectedDates.length > 0) {
      return selectedDates[0];
    }

    if (booking?.date instanceof Date && !Number.isNaN(booking.date.getTime())) {
      return formatDateISO(booking.date);
    }

    if (typeof booking?.date === 'string' && booking.date) {
      const parsed = new Date(`${booking.date}T00:00:00`);
      if (!Number.isNaN(parsed.getTime())) {
        return formatDateISO(parsed);
      }
    }

    if (selectedDates.length > 0) {
      return selectedDates[0];
    }

    return null;
  }, [availableDates, bookAllAvailableDates, booking?.date, canBookRecurring, selectedDates]);

  // Fetch slot availability with optional position-aware capacity validation
  // When pickupWaypointId is provided, the backend uses position-aware capacity
  // to determine available seats at each waypoint along the route
  const fetchSlotAvailability = useCallback(async (
    routeId,
    targetDate,
    pickupWaypointId = null,
    dropoffWaypointId = null,
    candidateSlotTimes = []
  ) => {
    const requestGeneration = availabilityRequestGenerationRef.current + 1;
    availabilityRequestGenerationRef.current = requestGeneration;
    if (!currentEntity?.entityId || !routeId || !targetDate) {
      setSlotAvailability([]);
      setSlotAvailabilityDate(null);
      return;
    }

    const {
      riders: requestedRiders,
      luggages: requestedLuggages,
      errorKey: quantityErrorKey,
    } = resolveAvailabilityQuantities({
      riders: booking?.riders,
      luggage: booking?.luggage,
    });
    if (quantityErrorKey) {
      // Fail closed exactly like a failed authority read, but name the field the
      // user has to fix instead of reporting a fetch failure.
      toast.error(t(quantityErrorKey), { toastId: 'invalid-availability-quantities' });
      setSlotAvailability([]);
      setOccurrencePreview({});
      setSlotAvailabilityDate(null);
      // This call supersedes any in-flight read, whose `finally` is now
      // generation-mismatched and will not release the flag. Leaving it set
      // would both strand the refresh indicator and suppress the consumer's
      // `availabilityUnresolved` marking, which is gated on not-loading.
      setSlotAvailabilityLoading(false);
      return;
    }

    setSlotAvailabilityLoading(true);
    try {
      // Build params, only including pickupWaypointId if it's valid
      const params = {
        routeId,
        date: targetDate,
        requestAccessibleVehicle: Boolean(booking?.requestAccessibleVehicle),
        riders: requestedRiders,
        luggages: requestedLuggages,
      };

      if (candidateSlotTimes.length > 0) {
        params.candidateTimes = candidateSlotTimes;
      }

      // Add pickup/dropoff waypoint IDs for position-aware capacity validation
      // Dropoff enables segment capacity (pickup-to-dropoff) when the feature is enabled
      if (pickupWaypointId && !isNaN(parseInt(pickupWaypointId, 10))) {
        params.pickupWaypointId = parseInt(pickupWaypointId, 10);
      }
      if (dropoffWaypointId && !isNaN(parseInt(dropoffWaypointId, 10))) {
        params.dropoffWaypointId = parseInt(dropoffWaypointId, 10);
      }

      const response = await axios.get(
        `/api/bookings/${currentEntity.entityId}/slot-availability`,
        { params },
      );
      if (availabilityRequestGenerationRef.current !== requestGeneration) return;

      let slots = response?.data?.data?.slots || [];
      const occurrenceDates = selectOperationalOccurrenceDates({
        availableDates,
        selectedDates,
        targetDate,
        bookAllAvailableDates,
        canBookRecurring,
      });
      const operationalResponse = await axios.post(
        `/api/bookings/${currentEntity.entityId}/slot-availability/query`,
        {
          routeId,
          dates: occurrenceDates,
          riders: requestedRiders,
          luggages: requestedLuggages,
          requestAccessibleVehicle: Boolean(booking?.requestAccessibleVehicle),
          pickupWaypointId: params.pickupWaypointId,
          dropoffWaypointId: params.dropoffWaypointId,
        },
      );
      if (availabilityRequestGenerationRef.current !== requestGeneration) return;
      const occurrences = operationalResponse?.data?.data?.occurrences || {};
      setOccurrencePreview(occurrences);
      const primaryStates = occurrences[targetDate] || [];
      const aggregatedStates = aggregateOperationalStates({
        primaryStates,
        occurrenceDates,
        occurrences,
        bookAllAvailableDates,
      });
      const fixedStates = new Map(
        aggregatedStates
          .filter((state) => state.type === 'fixed')
          .map((state) => [state.time, state]),
      );
      const authoritativeTripIds = new Set(
        aggregatedStates.flatMap((state) => [
          state.tripId,
          ...(state.existingTrips || []).map((trip) => trip.tripId)
        ])
          .map(Number)
          .filter((tripId) => Number.isInteger(tripId) && tripId > 0)
      );
      slots = slots.filter((slot) => (
        !slot.tripId || authoritativeTripIds.has(Number(slot.tripId))
      ));
      slots = slots.map((slot) => ({
        ...slot,
        ...(fixedStates.get(slot.time) || {}),
      }));
      const existingTimes = new Set(slots.map((slot) => slot.time));
      slots.push(...aggregatedStates.filter((state) => (
        state.type === 'on_call'
        || (state.type === 'fixed' && !existingTimes.has(state.time))
      )));
      if (availabilityRequestGenerationRef.current === requestGeneration) {
        setSlotAvailability(slots);
        setSlotAvailabilityDate(targetDate);
      }
    } catch (error) {
      if (availabilityRequestGenerationRef.current !== requestGeneration) return;
      console.error('Error fetching slot availability:', error);
      toast.error(t('failedToFetchSlotAvailability'));
      setSlotAvailability([]);
      setOccurrencePreview({});
      // A failed authority read is unresolved, not an empty/available result.
      // Consumers fail closed until a later refresh succeeds for this date.
      setSlotAvailabilityDate(null);
    } finally {
      if (availabilityRequestGenerationRef.current === requestGeneration) {
        setSlotAvailabilityLoading(false);
      }
    }
  }, [
    booking?.luggage,
    booking?.requestAccessibleVehicle,
    booking?.riders,
    availableDates,
    bookAllAvailableDates,
    canBookRecurring,
    currentEntity?.entityId,
    selectedDates,
    t,
  ]);

  // Refetch slot availability when route, date, or pickup location changes
  // Pass pickupWaypointId for position-aware capacity validation
  useEffect(() => {
    const targetDate = resolvePrimaryDate();
    if (!booking.route || !targetDate) {
      availabilityRequestGenerationRef.current += 1;
      setSlotAvailability([]);
      setSlotAvailabilityDate(null);
      setOccurrencePreview({});
      return;
    }

    // Pass booking pickup/dropoff waypoints for segment-aware capacity
    fetchSlotAvailability(
      parseInt(booking.route, 10),
      targetDate,
      booking.pickup,
      booking.dropoff,
      buildCandidateTimes(availableTimeSlots),
    );
  }, [
    availableTimeSlots,
    booking.dropoff,
    booking.luggage,
    booking.pickup,
    booking.requestAccessibleVehicle,
    booking.riders,
    booking.route,
    fetchSlotAvailability,
    resolvePrimaryDate,
  ]);

  useEffect(() => {
    if (bookAllAvailableDates && appliedSchedules.length > 0 && availableDates.length > 0) {
      const firstDate = new Date(`${availableDates[0]}T00:00:00`);
      const applicableSchedules = appliedSchedules.filter(
        (schedule) =>
          schedule.active &&
          isDateInRange(firstDate, schedule.start_date, schedule.end_date) &&
          isDayIncluded(firstDate, schedule.included_days) &&
          !isDateExcluded(firstDate, schedule.excluded_periods),
      );

      const allTimeSlots = generateTimeSlotsFromSchedule(applicableSchedules, firstDate);
      setAvailableTimeSlots(allTimeSlots);
      setSelectedDates(availableDates);
    } else if (!bookAllAvailableDates) {
      setAvailableTimeSlots([]);
      setSelectedDates([]);
    }
  }, [appliedSchedules, availableDates, bookAllAvailableDates]);

  const checkAvailability = useCallback(
    async (date, time) => {
      if (!currentEntity?.entityId) return false;

      try {
        const response = await axios.post('/api/bookings/check-availability', {
          entityId: currentEntity.entityId,
          bookingDate: date,
          bookingTime: time,
          riders: parseInt(booking.riders || 1, 10),
        });

        const { available, canWaitlist } = response.data;
        if (!available && !canWaitlist) {
          toast.error(t('thisTimeSlotIsNotAvailable'));
          return false;
        }

        setIsWaitlisted(!available && canWaitlist);
        return true;
      } catch (error) {
        console.error('failedToCheckAvailability:', error);
        toast.error(t('failedToCheckAvailability'));
        return false;
      }
    },
    [booking.riders, currentEntity?.entityId, setIsWaitlisted, t],
  );

  const handleDateSelection = useCallback(
    (dates) => {
      if (!dates) return;

      if (canBookRecurring) {
        const validDates = dates.filter((date) => {
          const dateObj = new Date(`${date}T00:00:00`);
          return appliedSchedules.some(
            (schedule) =>
              schedule.active &&
              isDateInRange(dateObj, schedule.start_date, schedule.end_date) &&
              isDayIncluded(dateObj, schedule.included_days) &&
              !isDateExcluded(dateObj, schedule.excluded_periods),
          );
        });

        if (validDates.length > 0) {
          const firstDate = new Date(`${validDates[0]}T00:00:00`);
          const applicableSchedules = appliedSchedules.filter(
            (schedule) =>
              schedule.active &&
              isDateInRange(firstDate, schedule.start_date, schedule.end_date) &&
              isDayIncluded(firstDate, schedule.included_days) &&
              !isDateExcluded(firstDate, schedule.excluded_periods),
          );
          const allTimeSlots = generateTimeSlotsFromSchedule(applicableSchedules, firstDate);
          setAvailableTimeSlots(allTimeSlots);
        }

        if (validDates.length !== dates.length) {
          toast.warning(t('someSelectedDatesAreNotAvailable'));
        }

        const previousPrimaryDate = normalizeBookingOccurrenceDate(selectedDates[0]);
        const nextPrimaryDate = normalizeBookingOccurrenceDate(validDates[0]);
        if (previousPrimaryDate !== nextPrimaryDate) {
          // Trip and schedule IDs are occurrence identities, not reusable
          // HH:mm preferences. Clearing them prevents Date A's trip from being
          // carried into Date B while the new authoritative snapshot loads.
          setBooking((prev) => ({
            ...prev,
            schedule: '',
            selectedTripId: null,
            selectedScheduleId: null,
          }));
        }
        setSelectedDates(validDates);
      } else {
        const date = new Date(dates);
        setBooking((prev) => {
          const previousDate = normalizeBookingOccurrenceDate(prev.date);
          const nextDate = normalizeBookingOccurrenceDate(date);
          return previousDate === nextDate
            ? { ...prev, date }
            : {
                ...prev,
                date,
                schedule: '',
                selectedTripId: null,
                selectedScheduleId: null,
              };
        });

        const applicableSchedules = appliedSchedules.filter(
          (schedule) =>
            schedule.active &&
            isDateInRange(date, schedule.start_date, schedule.end_date) &&
            isDayIncluded(date, schedule.included_days) &&
            !isDateExcluded(date, schedule.excluded_periods),
        );

        const allTimeSlots = generateTimeSlotsFromSchedule(applicableSchedules, date);
        setAvailableTimeSlots(allTimeSlots);
      }
    },
    [appliedSchedules, canBookRecurring, selectedDates, setBooking, t],
  );

  const handleTimeSelection = useCallback(
    async (time) => {
      if (!booking.date) return;
      if (await checkAvailability(booking.date, time)) {
        setBooking((prev) => ({ ...prev, schedule: time }));
      }
    },
    [booking.date, checkAvailability, setBooking],
  );

  const fetchAppliedSchedules = useCallback(
    async (routeId, showLoading = true) => {
      if (!routeId || !currentEntity?.entityId) return [];

      if (showLoading) {
        setLoading(true);
      }
      try {
        const response = await axios.get(`/api/${currentEntity.entityId}/${routeId}/route-schedules`);
        const schedules = response.data;

        if (schedules.length > 0) {
          setAppliedSchedules(schedules);
          generateAvailableDates(schedules);
        } else {
          setAppliedSchedules([]);
          setAvailableDates([]);
          setAvailableTimeSlots([]);
          toast.error(t('No schedules found for this route'));
        }

        return schedules;
      } catch (error) {
        console.error('Error fetching applied schedules:', error);
        toast.error(t('Failed to fetch applied schedules. Please try again.'));
        return [];
      } finally {
        if (showLoading) {
          setLoading(false);
        }
      }
    },
    [currentEntity?.entityId, generateAvailableDates, setLoading, t],
  );

  return {
    appliedSchedules,
    availableDates,
    availableTimeSlots,
    selectedDates,
    setSelectedDates,
    bookAllAvailableDates,
    setBookAllAvailableDates,
    handleDateSelection,
    handleTimeSelection,
    fetchAppliedSchedules,
    setAvailableTimeSlots,
    setAppliedSchedules,
    setAvailableDates,
    generateAvailableDates,
    slotAvailability,
    slotAvailabilityLoading,
    slotAvailabilityDate,
    occurrencePreview,
    refreshSlotAvailability: fetchSlotAvailability,
  };
};

export default useScheduleAvailability;
